import React from 'react'
import PropTypes from 'prop-types'
import styled from 'styled-components'
import { navy, muted, primary, surface, shadowSoft, radiusLg, borderSoft, bgApp } from '../Layout/Resources/Theme'

const Page = styled.div`
  animation: sp-fade-up 0.3s ease both;
`

const Hero = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 16px;
  margin-bottom: 20px;
  flex-wrap: wrap;
`

const HeroLeft = styled.div`
  min-width: 0;
  flex: 1;
`

const Eyebrow = styled.div`
  font-size: 0.68rem;
  font-weight: 700;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  color: ${primary};
  margin-bottom: 6px;
`

const Title = styled.h1`
  margin: 0;
  font-size: 1.75rem;
  font-weight: 800;
  color: ${navy};
  letter-spacing: -0.02em;
  line-height: 1.2;

  @media (max-width: 768px) {
    font-size: 1.35rem;
  }
`

const Subtitle = styled.p`
  margin: 8px 0 0;
  font-size: 0.92rem;
  color: ${muted};
  max-width: 520px;
  line-height: 1.45;
`

const HeroRight = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  flex-shrink: 0;
  flex-wrap: wrap;
`

const Badge = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 8px 14px;
  border-radius: 999px;
  background: ${surface};
  border: 1px solid ${borderSoft};
  color: ${muted};
  font-size: 0.75rem;
  font-weight: 600;
  box-shadow: ${shadowSoft};
`

const Panel = styled.div`
  background: ${surface};
  border-radius: ${radiusLg};
  box-shadow: ${shadowSoft};
  border: 1px solid ${borderSoft};
  overflow: hidden;
`

const Toolbar = styled.div`
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  gap: 12px;
  padding: 18px 20px;
  border-bottom: 1px solid ${borderSoft};
  background: linear-gradient(180deg, #FFFFFF 0%, ${bgApp} 100%);
  flex-wrap: wrap;
`

const Filters = styled.div`
  display: flex;
  align-items: flex-end;
  gap: 10px;
  flex-wrap: wrap;
  flex: 1;
`

const FilterField = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
  min-width: 140px;
`

const FilterLabel = styled.label`
  font-size: 0.7rem;
  font-weight: 700;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: ${muted};
  margin: 0;
`

const FilterControl = styled.div`
  input, select {
    height: 42px;
    border-radius: 12px !important;
    border: 1px solid ${borderSoft} !important;
    padding: 0 12px !important;
    background: #fff !important;
    min-width: 160px;
    font-size: 0.85rem;
  }
`

const PanelBody = styled.div`
  padding: ${props => props.noPadding ? '0' : '8px 20px 20px'};
`

const PrimaryBtn = styled.button`
  height: 42px;
  padding: 0 18px;
  border: none;
  border-radius: 12px;
  background: ${navy};
  color: #fff;
  font-weight: 700;
  font-size: 0.85rem;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  gap: 8px;
  transition: background 0.18s ease, transform 0.18s ease;
  white-space: nowrap;

  &:hover {
    background: #2c1a42;
  }

  i {
    font-size: 0.8rem;
  }
`

const SearchBtn = styled.button`
  height: 42px;
  width: 42px;
  border: none;
  border-radius: 12px;
  background: ${navy};
  color: #fff;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;

  &:hover {
    background: #2c1a42;
  }
`

const ListPageShell = ({
  eyebrow,
  title,
  subtitle,
  badge,
  primaryAction,
  filters,
  onSearch,
  children,
  noPadding
}) => {
  return (
    <Page>
      <Hero>
        <HeroLeft>
          {eyebrow ? <Eyebrow>{eyebrow}</Eyebrow> : null}
          <Title>{title}</Title>
          {subtitle ? <Subtitle>{subtitle}</Subtitle> : null}
        </HeroLeft>
        <HeroRight>
          {badge ? <Badge><i className="far fa-clock" />{badge}</Badge> : null}
          {primaryAction}
        </HeroRight>
      </Hero>

      <Panel>
        {(filters || onSearch || primaryAction) ? (
          <Toolbar>
            <Filters>
              {React.Children.toArray((filters || []).map((f, idx) => (
                <FilterField key={idx}>
                  {f.label ? <FilterLabel>{f.label}</FilterLabel> : null}
                  <FilterControl>{f.control}</FilterControl>
                </FilterField>
              )))}
              {onSearch ? (
                <SearchBtn type="button" onClick={onSearch} title="Buscar">
                  <i className="fa fa-search" />
                </SearchBtn>
              ) : null}
            </Filters>
          </Toolbar>
        ) : null}
        <PanelBody noPadding={noPadding}>{children}</PanelBody>
      </Panel>
    </Page>
  )
}

ListPageShell.PrimaryButton = ({ children, onClick, icon }) => (
  <PrimaryBtn type="button" onClick={onClick}>
    {icon ? <i className={icon} /> : <i className="fa fa-plus" />}
    {children}
  </PrimaryBtn>
)

ListPageShell.propTypes = {
  eyebrow: PropTypes.node,
  title: PropTypes.node,
  subtitle: PropTypes.node,
  badge: PropTypes.node,
  primaryAction: PropTypes.node,
  filters: PropTypes.array,
  onSearch: PropTypes.func,
  children: PropTypes.node,
  noPadding: PropTypes.bool
}

ListPageShell.defaultProps = {
  eyebrow: null,
  subtitle: null,
  badge: null,
  primaryAction: null,
  filters: [],
  onSearch: null,
  noPadding: false
}

export default ListPageShell
